import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { getAppBaseUrl, getApiBaseUrl } from '@/utils/runtimeApi';
import { formatApiErrorDetail } from '@/utils/authErrors';

const AuthContext = createContext(null);

const API_URL = getAppBaseUrl();
const API = getApiBaseUrl();

axios.defaults.withCredentials = true;

const DEFAULT_REFRESH_INTERVAL_MS = 12 * 60 * 60 * 1000;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const shouldRetryAuthRequest = (error) => !error.response || error.response.status >= 500;

const runWithAuthBackoff = async (requestFn, delays = [0, 800, 1600]) => {
  let lastError;

  for (let index = 0; index < delays.length; index += 1) {
    if (delays[index] > 0) {
      await sleep(delays[index]);
    }

    try {
      return await requestFn();
    } catch (error) {
      lastError = error;
      if (index === delays.length - 1 || !shouldRetryAuthRequest(error)) {
        throw error;
      }
    }
  }

  throw lastError;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const clearAuthState = useCallback(() => {
    setUser(null);
  }, []);

  // Token refresh fonksiyonu
  const refreshToken = useCallback(async () => {
    if (!user) {
      return false;
    }
    
    try {
      const response = await runWithAuthBackoff(() => axios.post(`${API}/auth/refresh-token`, {}, {
        withCredentials: true
      }));
      const { user: userData } = response.data;
      setUser(userData);
      return true;
    } catch (error) {
      console.error('Token yenileme başarısız:', error);
      if ([401, 403].includes(error.response?.status)) {
        clearAuthState();
      }
      return false;
    }
  }, [clearAuthState, user]);

  // Otomatik token yenileme - akıllı interval
  useEffect(() => {
    if (!user) return;

    const refreshTimer = setInterval(() => {
      refreshToken();
    }, DEFAULT_REFRESH_INTERVAL_MS);

    return () => clearInterval(refreshTimer);
  }, [user, refreshToken]);

  const fetchUser = useCallback(async () => {
    try {
      const response = await runWithAuthBackoff(() => axios.get(`${API}/auth/me`, {
        withCredentials: true
      }));
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);

      if ([401, 403].includes(error.response?.status)) {
        clearAuthState();
      }
    } finally {
      setLoading(false);
    }
  }, [clearAuthState]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = async (email, password, rememberMe = false) => {
    const response = await axios.post(`${API}/auth/login`, { email, password, remember_me: rememberMe }, {
      withCredentials: true
    });
    const { user: userData } = response.data;
    setUser(userData);
    return userData;
  };

  const signup = async (payload) => {
    const response = await axios.post(`${API}/auth/signup`, payload, {
      withCredentials: true,
    });
    const { user: userData } = response.data;
    setUser(userData);
    return userData;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout request failed:', error);
    }
    clearAuthState();
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, signup, formatApiErrorDetail }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const api = axios.create({
  baseURL: API,
  withCredentials: true
});

api.interceptors.request.use((config) => {
  config.withCredentials = true;
  return config;
});

// 401 handler - otomatik token refresh veya logout
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // 401 hatası ve henüz retry yapılmamışsa
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        // Token'ı yenilemeyi dene
        await runWithAuthBackoff(() => axios.post(`${API}/auth/refresh-token`, {}, {
          withCredentials: true
        }));
        
        // Orjinal isteği tekrar dene
        return api(originalRequest);
      } catch (refreshError) {
        const refreshStatus = refreshError.response?.status;

        if ([401, 403].includes(refreshStatus)) {
          console.error('Token yenileme başarısız, logout yapılıyor');

          const message = error.response?.data?.detail || 'Oturum süresi doldu';
          if (message.includes('başka bir cihazdan')) {
            alert('Hesabınız başka bir cihazdan giriş yaptığı için oturumunuz kapatıldı.');
          } else {
            alert('Oturumunuz sona erdi. Lütfen tekrar giriş yapın.');
          }

          window.location.href = '/login';
        } else {
          console.error('Token yenileme ağ hatası nedeniyle başarısız oldu, oturum korunuyor');
          toast.error('Bağlantı sorunu nedeniyle kullanıcı doğrulaması yenilenemedi. Lütfen tekrar deneyin.');
        }

        return Promise.reject(refreshError);
      }
    }
    
    return Promise.reject(error);
  }
);
